/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import type { AdobeId } from "reducers/CurrentUserReducer";
import { jwtDecode } from "jwt-decode";

const EXPIRY_BUFFER_ROOM_MS = 10000;

export const getAdobeIdFromAccessToken = async (
  accessTokenInfo?: AccessTokenInfo
): Promise<AdobeId | null> => {
  if (!accessTokenInfo) {
    accessTokenInfo = (await getValidAccessToken()) ?? undefined;
  }

  if (accessTokenInfo === undefined) {
    return null;
  }

  const decodedToken = jwtDecode(accessTokenInfo.token);

  if (accessTokenInfo.isGuestToken) {
    return decodedToken.sub ?? null;
  } else {
    // non-standard field for authenticated access tokens
    return (decodedToken as any)["user_id"] ?? null;
  }
};

export const getValidAccessToken = async (
  canRefresh: boolean = true
): Promise<AccessTokenInfo | null> => {
  const currentToken = window.adobeIMS?.getAccessToken();
  if (currentToken && tokenSafeFromExpiry(canRefresh, currentToken)) {
    return currentToken;
  }

  if (!canRefresh) {
    return null;
  }

  newrelic.addPageAction("REFRESHED_ACCESS_TOKEN");
  await window.adobeIMS?.refreshToken();
  return window.adobeIMS?.getAccessToken() ?? null;
};

const tokenSafeFromExpiry = (
  canRefresh: boolean,
  currentToken: AccessTokenInfo
): boolean => {
  if (!canRefresh) {
    return true;
  }

  return (
    currentToken.expire.getTime() > new Date().getTime() + EXPIRY_BUFFER_ROOM_MS
  );
};

export default getValidAccessToken;
